import { useEffect, useState } from 'react';
import { Phone, Info, CheckCircle2 } from 'lucide-react';

import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import {
  Button,
  InfoAlert,
  Input,
  Modal,
  PhoneInput,
  Select,
  Switch,
  isValidE164Phone,
} from '@/components/ui';
import { toast } from '@/stores';
import { userService } from '@/services';
import { config } from '@/lib/config';
import { usePhoneVerification, useUserRole } from '@/hooks';
import type { NotificationPreferencesUpdate, SmsContactChannel } from '@/types';
import { SMS_CONSENT_TEXT, SMS_CONSENT_VERSION } from '@/config/smsConsent';
import { SmsConsentDisclosure } from '@/components/domain/shared/SmsConsentDisclosure';
import {
  DEFAULT_DIGEST_MINUTES,
  DIGEST_TIME_OPTIONS,
} from '@/config/digestTimes';
import { SettingsNotificationsSkeleton } from './skeletons';

function isKnownDigestMinutes(value: number): boolean {
  return DIGEST_TIME_OPTIONS.some((o) => o.value === value);
}

/**
 * Formats an arbitrary minutes-from-midnight value as "h:mm AM/PM" — used
 * only for the one-off "(current)" fallback option below, when the loaded
 * value is off the offered evening slots (e.g. a legacy pre-YAR-266 value that
 * predates the evening-only re-launch and slipped past the migration
 * backfill). Without this, the Select would either render blank (value not
 * among its options) or silently snap to a different option on next save.
 */
function formatMinutesLabel(minutes: number): string {
  const total = ((minutes % 1440) + 1440) % 1440;
  const hour24 = Math.floor(total / 60);
  const mins = total % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(mins).padStart(2, '0')} ${period}`;
}

export function SettingsNotifications() {
  const [loading, setLoading] = useState(true);

  // Beta: SMS + evening digest is admin-only. Only the household PRIMARY sees
  // the opt-in, because the digest job only ever sends to the PRIMARY member.
  const { isPrimary } = useUserRole();

  // SMS preferences.
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [digestSmsEnabled, setDigestSmsEnabled] = useState(false);
  // Minutes from local midnight (DB default 1140 = 7:00 PM evening digest).
  const [digestMinutes, setDigestMinutes] = useState(DEFAULT_DIGEST_MINUTES);

  // SMS contact channel + verification UI.
  const [contact, setContact] = useState<SmsContactChannel | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const verified = !!contact?.verified_at && !contact?.opted_out_at;
  const optedOut = !!contact?.opted_out_at;
  const phoneValid = isValidE164Phone(phoneInput);

  // Safety net for a still-off-list digestMinutes (should be rare after the
  // 20260706150000 migration snapped legacy values to the evening set, but
  // costs nothing to keep): prepend a "(current)" option carrying the exact
  // loaded value so the Select never renders blank or silently overwrites
  // it with a different time on the next save.
  const digestTimeOptions = isKnownDigestMinutes(digestMinutes)
    ? DIGEST_TIME_OPTIONS
    : [
        {
          value: digestMinutes,
          label: `${formatMinutesLabel(digestMinutes)} (current)`,
        },
        ...DIGEST_TIME_OPTIONS,
      ];

  const verify = usePhoneVerification({
    onVerified: async () => {
      setVerifyOpen(false);
      setEditingPhone(false);
      toast.success('Phone number verified');
      const fresh = await userService.getSmsContactChannel().catch(() => null);
      setContact(fresh);
      // Verifying is an explicit opt-in — turn the SMS channel on.
      setSmsEnabled(true);
      void userService
        .updateNotificationPreferences({ sms_enabled: true })
        .catch(() => {});
    },
  });

  useEffect(() => {
    Promise.all([
      userService.getNotificationPreferences(),
      config.enableSms
        ? userService.getSmsContactChannel().catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([prefs, sms]) => {
        if (prefs) {
          setSmsEnabled(prefs.sms_enabled ?? false);
          setDigestSmsEnabled(prefs.digest_sms_enabled ?? false);
          setDigestMinutes(prefs.digest_send_minutes ?? DEFAULT_DIGEST_MINUTES);
        }
        setContact(sms);
        if (sms?.address && !(sms.verified_at && !sms.opted_out_at)) {
          // Prefill an unverified / opted-out number so the user can resume.
          setPhoneInput(sms.address);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Persist a single preference with optimistic update + rollback.
  async function persist(
    patch: NotificationPreferencesUpdate,
    apply: () => void,
    revert: () => void,
  ) {
    apply();
    try {
      await userService.updateNotificationPreferences(patch);
    } catch {
      revert();
      toast.error('Failed to update notification preferences');
    }
  }

  const handleSmsToggle = () => {
    const prev = smsEnabled;
    void persist(
      { sms_enabled: !prev },
      () => setSmsEnabled(!prev),
      () => setSmsEnabled(prev),
    );
  };

  const handleDigestSmsToggle = () => {
    const prev = digestSmsEnabled;
    const next = !prev;
    // Turning the digest ON always carries a valid digest_send_minutes in the
    // same payload — digestMinutes is initialized from the DB default (1140)
    // and never unset, so a toggle-on can never persist without a send time.
    void persist(
      next
        ? { digest_sms_enabled: true, digest_send_minutes: digestMinutes }
        : { digest_sms_enabled: false },
      () => setDigestSmsEnabled(next),
      () => setDigestSmsEnabled(prev),
    );
  };

  const handleMinutesChange = (value: string) => {
    const prev = digestMinutes;
    const next = Number(value);
    void persist(
      { digest_send_minutes: next },
      () => setDigestMinutes(next),
      () => setDigestMinutes(prev),
    );
  };

  const handleSendCode = async () => {
    if (!phoneValid || !consentChecked) return;
    setCodeInput('');
    setVerifyOpen(true);
    await verify.start({
      e164: phoneInput.trim(),
      consentAccepted: consentChecked,
      consentVersion: SMS_CONSENT_VERSION,
      consentText: SMS_CONSENT_TEXT,
    });
  };

  const handleResend = async () => {
    if (verify.cooldown > 0) return;
    await verify.start({
      e164: phoneInput.trim(),
      consentAccepted: true,
      consentVersion: SMS_CONSENT_VERSION,
      consentText: SMS_CONSENT_TEXT,
    });
  };

  const handleVerify = async () => {
    if (codeInput.trim().length < 4) return;
    await verify.submitCode({
      e164: phoneInput.trim(),
      code: codeInput.trim(),
    });
  };

  const handleRemovePhone = async () => {
    setRemoving(true);
    try {
      await userService.removePhoneNumber();
      // Removal deactivates SMS + the digest server-side (YAR-265 AC7) —
      // mirror that locally and drop back to the empty phone-entry state.
      setContact(null);
      setPhoneInput('');
      setConsentChecked(false);
      setEditingPhone(false);
      setSmsEnabled(false);
      setDigestSmsEnabled(false);
      setRemoveOpen(false);
      toast.success('Phone number removed');
    } catch {
      // The server-side removal (remove_my_sms_channel RPC) is one atomic
      // transaction, but THIS request can still fail after the server
      // already committed (e.g. a dropped connection) — re-fetch the real
      // channel + preferences instead of trusting whatever the UI showed
      // before the attempt, so a removal that actually went through
      // doesn't keep displaying as "still verified".
      const [freshContact, freshPrefs] = await Promise.all([
        userService.getSmsContactChannel().catch(() => null),
        userService.getNotificationPreferences().catch(() => null),
      ]);
      setContact(freshContact);
      if (freshPrefs) {
        setSmsEnabled(freshPrefs.sms_enabled ?? false);
        setDigestSmsEnabled(freshPrefs.digest_sms_enabled ?? false);
      }
      if (!freshContact) {
        // Removal actually succeeded server-side — finish the same reset
        // the happy path does instead of leaving a dangling stale number.
        setPhoneInput('');
        setConsentChecked(false);
        setEditingPhone(false);
        setRemoveOpen(false);
      }
      toast.error('Failed to remove phone number');
    } finally {
      setRemoving(false);
    }
  };

  // YAR-170 AC5: titled to match the Activity overflow item that opens it —
  // "a menu label must name where it goes".
  return (
    <SettingsSubPage title="Notification settings">
      {loading ? (
        <SettingsNotificationsSkeleton />
      ) : (
        <div className="flex flex-col gap-6 px-5 py-6">
          {config.enableSms && isPrimary && (
            <>
              {/* ── Text messages (SMS) ─────────────────────────────── */}
              <div className="flex flex-col gap-3">
                <h2 className="text-sm font-semibold text-foreground">
                  Text messages (SMS)
                </h2>

                {verified && !editingPhone ? (
                  <div className="flex flex-col gap-3">
                    {/* `gap-4` + `min-w-0`: with only `justify-between`, a
                        label that fills the row butts straight up against
                        the switch (YAR-230). The gap reserves Figma's 16px
                        channel and the min-width lets the text column, not
                        the switch, absorb the shrinking. */}
                    <div className="flex items-center justify-between gap-4 border border-border rounded-xl p-4">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                          <CheckCircle2
                            size={16}
                            className="text-success"
                            aria-hidden="true"
                          />
                          {contact?.address}
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingPhone(true);
                              setConsentChecked(false);
                            }}
                            className="self-start text-[0.8125rem] text-muted-foreground hover:underline cursor-pointer"
                          >
                            Use a different number
                          </button>
                          <button
                            type="button"
                            onClick={() => setRemoveOpen(true)}
                            className="self-start text-[0.8125rem] text-danger hover:underline cursor-pointer"
                          >
                            Remove phone number
                          </button>
                        </div>
                      </div>
                      <Switch
                        checked={smsEnabled}
                        onChange={handleSmsToggle}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 border border-border rounded-xl p-4">
                    {/* A STOP that reached Twilio blocks the number at the
                        carrier side until it texts START — re-verifying in
                        the app cannot lift that (the welcome text after a
                        re-opt-in fails with 21610). Say the one thing that
                        works. */}
                    {optedOut && (
                      <InfoAlert icon={<Info size={18} />}>
                        You previously replied STOP. Text START to the number
                        that messaged you to receive texts again, then verify
                        your number.
                      </InfoAlert>
                    )}
                    {contact?.address &&
                      !contact.verified_at &&
                      !optedOut &&
                      !editingPhone &&
                      !verifyOpen && (
                        <InfoAlert icon={<Info size={18} />}>
                          This number isn&apos;t verified yet. Verify it to
                          receive your evening digest.
                        </InfoAlert>
                      )}
                    <PhoneInput
                      label="Mobile number"
                      labelIcon={<Phone size={16} />}
                      placeholder="555 123 4567"
                      value={phoneInput}
                      onChange={setPhoneInput}
                      hint="Select your country, then enter the number including area code."
                      error={
                        phoneInput && !phoneValid
                          ? 'Enter the complete mobile number, including area code.'
                          : undefined
                      }
                    />
                    {/* The same consent block as onboarding (YAR-460):
                        the ledger sentence as the checkbox label, unchecked
                        by default, Terms/Privacy links with it. */}
                    <SmsConsentDisclosure
                      checked={consentChecked}
                      onChange={setConsentChecked}
                    />
                    <Button
                      onClick={handleSendCode}
                      disabled={!phoneValid || !consentChecked}
                      fullWidth
                    >
                      Send verification code
                    </Button>
                    {editingPhone && (
                      <button
                        type="button"
                        onClick={() => setEditingPhone(false)}
                        className="self-center text-[0.8125rem] text-muted-foreground hover:underline cursor-pointer"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Evening digest (SMS) — admin-only for the beta: this
                     whole SMS block is already gated on `isPrimary` above,
                     so only the household PRIMARY reaches here, because the
                     digest job only ever sends to the PRIMARY member. Also
                     requires a verified phone: an unverified number falls
                     into the phone-entry/verify branch above instead of
                     rendering this section. ────────────────────────────── */}
              {verified && (
                <div className="flex flex-col gap-3">
                  <h2 className="text-sm font-semibold text-foreground">
                    Evening digest (SMS)
                  </h2>
                  {/* Same 16px channel as the number card above. */}
                  <div className="flex items-center justify-between gap-4 border border-border rounded-xl p-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">
                        Evening digest
                      </div>
                      <div className="text-[0.8125rem] text-muted-foreground">
                        One text a day with your new updates
                      </div>
                    </div>
                    <Switch
                      aria-label="Evening digest"
                      checked={digestSmsEnabled}
                      onChange={handleDigestSmsToggle}
                      disabled={!smsEnabled}
                    />
                  </div>

                  {digestSmsEnabled && smsEnabled && (
                    <div className="flex flex-col gap-4 border border-border rounded-xl p-4">
                      <div className="flex flex-col gap-1.5">
                        <Select
                          label="Send time"
                          value={String(digestMinutes)}
                          onChange={(e) => handleMinutesChange(e.target.value)}
                          options={digestTimeOptions.map(
                            ({ value, label }) => ({
                              value: String(value),
                              label,
                            }),
                          )}
                        />
                        <p className="text-[0.8125rem] text-muted-foreground">
                          Sent in the evening in your local timezone.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <p className="text-sm text-muted-foreground text-center">
            Email notifications are sent to your email address
            {config.enableSms ? '; texts go to your verified number.' : '.'}
          </p>
        </div>
      )}

      {/* Verification code modal */}
      <Modal
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        title="Verify your number"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setVerifyOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerify}
              loading={verify.status === 'verifying'}
              disabled={codeInput.trim().length < 4}
            >
              Verify
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            We sent a code to{' '}
            <span className="font-medium text-foreground">
              {phoneInput.trim()}
            </span>
            . Enter it below.
          </p>
          <Input
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            error={verify.error ?? undefined}
          />
          <button
            type="button"
            onClick={handleResend}
            disabled={verify.cooldown > 0 || verify.status === 'sending'}
            className="self-start text-[0.8125rem] text-muted-foreground hover:underline disabled:no-underline disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {verify.cooldown > 0
              ? `Resend code (${verify.cooldown}s)`
              : 'Resend code'}
          </button>
        </div>
      </Modal>

      {/* Remove phone number confirmation */}
      <Modal
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title="Remove phone number?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRemoveOpen(false)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemovePhone}
              loading={removing}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          This turns off text messages and your evening digest. You can add a
          number again anytime.
        </p>
      </Modal>
    </SettingsSubPage>
  );
}
